import React from "react";
import { useTheme } from "react-jss";
import { range } from "../../utils/lodash";
import { PaginatedTableProps } from "./PaginatedTable";
import ButtonGroup from "../shared/ButtonGroup";
import useStyles from "./TablePagination.styles";

interface PaginationProps {
    tableProps: PaginatedTableProps;
    rowsPerPage: number;
    currentPage: number;
    pageCount: number;
}

export function TablePagination(props: PaginationProps) {
    const { tableProps, rowsPerPage, currentPage, pageCount } = props;
    const { onChangePage: onPageChanged } = tableProps;

    const theme = useTheme();
    const classes = useStyles({ theme });

    let startPage = currentPage - 1;
    let endPage = startPage + 3;

    let showStartEllipsis = startPage > 2;
    if (!showStartEllipsis) {
        startPage = 2;
        endPage = 6;
    }

    const showEndEllipsis = endPage < pageCount - 2;
    if (!showEndEllipsis) {
        startPage = pageCount - 4;
        endPage = pageCount;
        showStartEllipsis = startPage > 3;
    }

    startPage = Math.max(startPage, 2);
    endPage = Math.min(endPage, pageCount);

    function navigateToPage(page: number) {
        if (page > pageCount) {
            navigateToPage(pageCount);
            return;
        }
        if (page < 1) {
            navigateToPage(1);
            return;
        }
        onPageChanged((page - 1) * rowsPerPage, rowsPerPage);
    }

    return (
        <div className={classes.pagination}>
            <ButtonGroup size="small">
                <ButtonGroup.Button
                    icon="caret-left"
                    disabled={currentPage <= 1}
                    onClick={() => navigateToPage(currentPage - 1)}
                />
                <ButtonGroup.Button
                    text="1"
                    active={currentPage === 1}
                    onClick={() => navigateToPage(1)}
                />
                {showStartEllipsis && <ButtonGroup.Button text="&hellip;" disabled />}
                {endPage > 1 &&
                    range(startPage, endPage).map((page) => (
                        <ButtonGroup.Button
                            key={page}
                            text={String(page)}
                            active={currentPage === page}
                            onClick={() => navigateToPage(page)}
                        />
                    ))}
                {showEndEllipsis && <ButtonGroup.Button text="&hellip;" disabled />}
                {endPage > 1 && (
                    <ButtonGroup.Button
                        text={String(pageCount)}
                        active={currentPage === pageCount}
                        onClick={() => navigateToPage(pageCount)}
                    />
                )}
                <ButtonGroup.Button
                    icon="caret-right"
                    disabled={currentPage >= pageCount}
                    onClick={() => navigateToPage(currentPage + 1)}
                />
            </ButtonGroup>
        </div>
    );
}
